class ReusableComponents {
  init() {
    this.#initStickyAnnouncements();
    this.#initTimers();
    this.#initStockComponents();
    this.#initVideoControls();
    this.#initCountUpStats();
    this.#initAutoScroll();
    this.#initGallerySliders();
    this.#initTestimonialOverlays();
    this.#initTestimonialFilters();
    this.#initReviewsCardsFilters();
    this.#initTabsContent();
    this.#initStepsTabs();
    this.#initVideoPopups();
    this.#initFitxSteps();
    this.#initAddToCart();
    this.#initReviews();
  }

  #initGallerySliders() {
    for (const slider of document.querySelectorAll(".gallery-slider")) {
      const track = slider.querySelector(".gallery-slider__track");
      if (!track) continue;

      const getSlides = () => [
        ...track.querySelectorAll(".gallery-slider__slide:not(.hidden)"),
      ];
      if (!getSlides().length) continue;

      const prev = slider.querySelector(".gallery-slider__button.prev");
      const next = slider.querySelector(".gallery-slider__button.next");
      const dotsWrap = slider.querySelector(".gallery-slider__dots");
      const loop = slider.dataset.loop === "true";
      const autoplay = slider.dataset.autoplay === "true";
      const interval = parseInt(slider.dataset.autoplayInterval, 10) || 4000;
      const isCenter = slider.dataset.align === "center";

      const gap = () => parseFloat(getComputedStyle(track).columnGap) || 0;

      // Step = one slide + gap.
      const step = () => {
        const slides = getSlides();
        if (!slides.length) return 0;
        return slides[0].offsetWidth + gap();
      };

      const colsMb = parseFloat(slider.dataset.colsMb) || 1;
      const colsSm = parseFloat(slider.dataset.colsSm) || colsMb;
      const colsTb = parseFloat(slider.dataset.colsTb) || colsMb;
      const colsDk = parseFloat(slider.dataset.cols) || colsTb;
      const colsLg = parseFloat(slider.dataset.colsLg) || colsDk;

      const colsFraction = () => {
        const w = window.innerWidth;
        if (w >= 1180 && slider.dataset.colsLg) return colsLg;
        if (w >= 1024) return colsDk;
        if (w >= 540) return colsTb;
        if (w >= 480 && slider.dataset.colsSm) return colsSm;
        return colsMb;
      };

      // Page size = floor of the configured columns for the current breakpoint,
      // so 1.7 cols scrolls 1 slide, 2.x scrolls 2, etc. (peek never bumps it up).
      const cols = () => Math.max(1, Math.floor(colsFraction()));
      const pages = () => {
        const slideCount = getSlides().length;
        return Math.max(1, isCenter ? slideCount : Math.ceil(slideCount / cols()));
      };
      const atStart = () => track.scrollLeft <= 1;
      const atEnd = () =>
        track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      const overflows = () => track.scrollWidth - track.clientWidth > 1;

      const visibleGroupWidth = () => {
        const c = colsFraction();
        const slideW = step() - gap();
        return c * slideW + Math.max(0, Math.ceil(c) - 1) * gap();
      };

      const updateCenterTrack = () => {
        if (!isCenter) {
          track.style.paddingInline = "";
          track.style.scrollPaddingInline = "";
          slider.classList.remove("gallery-slider--align-center");
          return;
        }
        slider.classList.add("gallery-slider--align-center");
        const pad = Math.max(0, (track.clientWidth - visibleGroupWidth()) / 2);
        track.style.paddingInline = `${pad}px`;
        track.style.scrollPaddingInline = `${pad}px`;
      };

      const nearestSlideIndex = () => {
        const slides = getSlides();
        if (!slides.length) return 0;
        const centerX = track.scrollLeft + track.clientWidth / 2;
        let nearest = 0;
        let nearestDist = Infinity;
        for (let i = 0; i < slides.length; i++) {
          const slide = slides[i];
          const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
          const dist = Math.abs(slideCenter - centerX);
          if (dist < nearestDist) {
            nearestDist = dist;
            nearest = i;
          }
        }
        return nearest;
      };

      const scrollToIndex = (index, behavior = "smooth") => {
        const slides = getSlides();
        const slide = slides[index];
        if (!slide) return;

        if (!isCenter) {
          track.scrollTo({ left: index * step() * cols(), behavior });
          return;
        }

        const lastPageStart = Math.max(0, slides.length - cols());
        if (!loop && index <= 0) {
          track.scrollTo({ left: 0, behavior });
          return;
        }
        if (!loop && index >= lastPageStart) {
          track.scrollTo({
            left: track.scrollWidth - track.clientWidth,
            behavior,
          });
          return;
        }

        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        track.scrollTo({
          left: slideCenter - track.clientWidth / 2,
          behavior,
        });
      };

      const clampSideArrows = () => {
        if (!slider.classList.contains("gallery-slider--arrows-sides")) return;

        const sliderStyles = getComputedStyle(slider);
        const offset =
          parseFloat(sliderStyles.getPropertyValue("--gs-arrow-side-offset")) ||
          (window.innerWidth >= 768 ? 44 : 16);
        const inset =
          parseFloat(
            sliderStyles.getPropertyValue("--gs-arrow-viewport-inset"),
          ) || 8;
        const ideal = -offset;
        const rect = slider.getBoundingClientRect();
        const viewportRight =
          document.documentElement.clientWidth - inset - rect.right;

        if (prev) {
          const left = Math.max(ideal, inset - rect.left);
          slider.style.setProperty("--gs-arrow-prev-left", `${left}px`);
        }

        if (next) {
          const right = Math.max(ideal, -viewportRight);
          slider.style.setProperty("--gs-arrow-next-right", `${right}px`);
        }
      };

      const scrollByCols = (dir) => {
        if (isCenter) {
          const slides = getSlides();
          const current = nearestSlideIndex();
          const target = Math.min(
            slides.length - 1,
            Math.max(0, current + dir),
          );
          scrollToIndex(target);
          return;
        }
        track.scrollBy({ left: dir * step() * cols(), behavior: "smooth" });
      };

      const go = (dir) => {
        if (dir > 0 && atEnd()) {
          if (loop) track.scrollTo({ left: 0, behavior: "smooth" });
          return;
        }
        if (dir < 0 && atStart()) {
          if (loop)
            track.scrollTo({ left: track.scrollWidth, behavior: "smooth" });
          return;
        }
        scrollByCols(dir);
      };

      prev?.addEventListener("click", () => go(-1));
      next?.addEventListener("click", () => go(1));

      let dots = [];

      const buildDots = () => {
        if (!dotsWrap) return;
        const count = pages();
        if (dots.length === count) return;
        dotsWrap.innerHTML = "";
        dots = Array.from({ length: count }, (_, i) => {
          const dot = document.createElement("button");
          dot.type = "button";
          dot.className = "gallery-slider__dot";
          dot.setAttribute("aria-label", `Go to slide ${i + 1}`);
          dot.addEventListener("click", () => {
            if (isCenter) scrollToIndex(i);
            else track.scrollTo({ left: i * step() * cols(), behavior: "smooth" });
          });
          dotsWrap.appendChild(dot);
          return dot;
        });
      };

      const sync = () => {
        if (prev && next) {
          const show = overflows() && !loop;
          prev.classList.toggle("is-disabled", show && atStart());
          next.classList.toggle("is-disabled", show && atEnd());
          const hide = !overflows();
          prev.classList.toggle("hidden", hide);
          next.classList.toggle("hidden", hide);
        }
        if (dots.length) {
          let active = 0;
          if (isCenter) {
            active = nearestSlideIndex();
          } else {
            const pageW = step() * cols() || track.clientWidth || 1;
            active = Math.min(
              dots.length - 1,
              Math.round(track.scrollLeft / pageW),
            );
          }
          dots.forEach((d, i) => d.classList.toggle("is-active", i === active));
        }
      };

      let raf = false;
      track.addEventListener(
        "scroll",
        () => {
          if (raf) return;
          raf = true;
          requestAnimationFrame(() => {
            sync();
            raf = false;
          });
        },
        { passive: true },
      );

      window.addEventListener(
        "resize",
        () => {
          updateCenterTrack();
          clampSideArrows();
          buildDots();
          sync();
        },
        { passive: true },
      );

      updateCenterTrack();
      clampSideArrows();
      buildDots();
      sync();

      if (isCenter && loop) {
        const slides = getSlides();
        const mid = Math.max(0, Math.floor((slides.length - cols()) / 2));
        scrollToIndex(mid, "auto");
        sync();
      }

      if (autoplay) {
        let timer = null;
        const stop = () => {
          clearInterval(timer);
          timer = null;
        };
        const start = () => {
          if (!timer && overflows()) timer = setInterval(() => go(1), interval);
        };
        slider.addEventListener("mouseenter", stop);
        slider.addEventListener("mouseleave", start);
        track.addEventListener("pointerdown", stop, { passive: true });
        start();
      }
    }
  }

  #initReviews() {
    for (const section of document.querySelectorAll(".reviews")) {
      const grid = section.querySelector("[data-reviews-grid]");
      const more = section.querySelector(".reviews__more");
      const btn = section.querySelector("[data-reviews-more]");
      if (!grid) continue;

      const cards = [...grid.querySelectorAll(".loox-review")];
      const total = cards.length;
      const visible = parseInt(grid.dataset.visible, 10) || total;
      const visibleMb = parseInt(grid.dataset.visibleMb, 10) || total;

      // Reveal one page (visible count) per click, per breakpoint.
      const batch = () => (window.innerWidth >= 1024 ? visible : visibleMb);

      // Take over from the CSS no-JS state.
      grid.classList.remove("is-collapsed");

      let shown = batch();

      const render = () => {
        for (let i = 0; i < total; i++)
          cards[i].classList.toggle("hidden", i >= shown);
        if (more) more.style.display = shown >= total ? "none" : "";
      };

      if (btn) {
        btn.addEventListener("click", () => {
          shown = Math.min(shown + batch(), total);
          render();
        });
      }

      window.addEventListener(
        "resize",
        () => {
          const b = batch();
          if (shown < b) shown = b;
          render();
        },
        { passive: true },
      );

      render();
    }
  }

  #initStickyAnnouncements() {
    const states = [
      ...document.querySelectorAll(
        ".header-clearance__announcement.sticky-up, .header-clearance__announcement.sticky",
      ),
    ].map((el) => {
      const spacer = document.createElement("div");
      spacer.className = "header-clearance__announcement-spacer";
      spacer.setAttribute("aria-hidden", "true");
      el.before(spacer);
      return {
        el,
        up: el.classList.contains("sticky-up"),
        spacer,
        sticky: false,
        leaving: false,
        timer: null,
        anchor: 0,
      };
    });
    if (!states.length) return;

    const measure = () =>
      states.forEach((s) => {
        if (!s.sticky && !s.leaving)
          s.anchor = s.el.getBoundingClientRect().top + window.scrollY;
      });

    const layout = () => {
      let top = 0;
      states.forEach((s, i) => {
        if (!s.sticky && !s.leaving) {
          s.el.style.top = s.el.style.zIndex = "";
          return;
        }
        s.el.style.top = `${top}px`;
        s.el.style.zIndex = String(10 + i);
        top += s.el.offsetHeight;
      });
    };

    const setSpacer = (s, on) => {
      s.spacer.style.height = on ? `${s.el.offsetHeight}px` : "0";
    };

    const stick = (s) => {
      if (s.sticky || s.leaving) return;
      setSpacer(s, true);
      s.el.classList.add("is-sticky", "is-entering");
      s.sticky = true;
      layout();
      requestAnimationFrame(() =>
        requestAnimationFrame(() => s.el.classList.remove("is-entering")),
      );
    };

    const reset = (s) => {
      clearTimeout(s.timer);
      s.timer = null;
      s.el.classList.remove("is-sticky", "is-leaving");
      setSpacer(s, false);
      s.el.style.top = s.el.style.zIndex = "";
      s.sticky = s.leaving = false;
      measure();
      layout();
    };

    const unstick = (s) => {
      if (!s.sticky || s.leaving) return;
      s.leaving = true;
      s.el.classList.add("is-leaving");
      layout();
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        reset(s);
      };
      s.el.addEventListener(
        "transitionend",
        (e) => {
          if (e.propertyName === "transform") finish();
        },
        { once: true },
      );
      s.timer = setTimeout(finish, 300);
    };

    const cancelLeave = (s) => {
      if (!s.leaving) return;
      clearTimeout(s.timer);
      s.timer = null;
      s.leaving = false;
      s.el.classList.remove("is-leaving");
      setSpacer(s, true);
      layout();
    };

    const update = (y, scrollingUp) =>
      states.forEach((s) => {
        const on = s.up ? scrollingUp && y > s.anchor : y > s.anchor;
        if (on) {
          if (s.leaving) cancelLeave(s);
          else if (!s.sticky) stick(s);
        } else if (s.sticky && !s.leaving) {
          unstick(s);
        }
      });

    measure();
    window.addEventListener(
      "resize",
      () => {
        measure();
        states.forEach((s) => {
          if (s.sticky || s.leaving) setSpacer(s, true);
        });
        layout();
      },
      { passive: true },
    );

    let lastY = window.scrollY,
      ticking = false;
    update(lastY, false);
    window.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const y = window.scrollY;
          update(y, y < lastY);
          lastY = y;
          ticking = false;
        });
      },
      { passive: true },
    );
  }

  #initTimers() {
    window.initTimerAll?.();
  }

  #initStockComponents() {
    window.initStockAll?.();
  }

  #initVideoControls() {
    const wrapperSelector =
      '.video-ui__video-wrapper[data-video-controls="true"]';

    // Needed because native autoplay + lazy sections often stall on mobile until we call `play()` once the section is in view.
    if (typeof IntersectionObserver !== "undefined") {
      const roots = new Set(
        [...document.querySelectorAll(".video-ui__video-wrapper.autoplay")].map(
          (w) => w.closest(".shopify-section") || w,
        ),
      );
      const io = new IntersectionObserver(
        (entries) => {
          for (const { isIntersecting, target } of entries) {
            const wraps = target.matches(".video-ui__video-wrapper.autoplay")
              ? [target]
              : target.querySelectorAll(".video-ui__video-wrapper.autoplay");

            for (const wrap of wraps) {
              const video = wrap.querySelector("video");
              if (!video) continue;

              // Skip display:none videos (desktop/mobile pairs) so the hidden one is never fetched
              if (isIntersecting && video.getClientRects().length) {
                Object.assign(video, { muted: true, playsInline: true });
                video.play().catch(() => {});
              } else {
                video.pause();
              }
            }
          }
        },
        { threshold: 0.2 },
      );

      for (const root of roots) io.observe(root);
    }

    for (const wrapper of document.querySelectorAll(wrapperSelector)) {
      wrapper.addEventListener("click", () => {
        const video = wrapper.querySelector("video");
        if (!video) return;

        // Autoplay mode: just toggle mute/unmute, don't pause/play
        if (wrapper.classList.contains("autoplay")) {
          video.muted = !video.muted;
          if (!video.muted) video.volume = 0.5;
          wrapper.classList.toggle("play", !video.muted);
          return;
        }

        // Default mode: pause when playing
        if (!video.paused) {
          video.pause();
          wrapper.classList.remove("play");
          return;
        }

        const placeholderGroup = wrapper.closest("[data-video-placeholder]");
        if (
          placeholderGroup &&
          wrapper.classList.contains("video-ui__main") &&
          !placeholderGroup.classList.contains("is-active")
        ) {
          placeholderGroup.classList.add("is-active");
          const placeholderVideo = placeholderGroup.querySelector(
            ".video-ui__placeholder video",
          );
          if (placeholderVideo) placeholderVideo.pause();
        }

        // Default mode: stop every other video, then play this one
        for (const other of document.querySelectorAll(wrapperSelector)) {
          const otherVideo = other.querySelector("video");
          if (!otherVideo || otherVideo === video) continue;

          otherVideo.muted = true;
          otherVideo.pause();
          other.classList.remove("play");
        }

        video.muted = false;
        video.volume = 0.5;
        video.play().catch(() => {});
        wrapper.classList.add("play");
      });
    }
  }

  /**
   * Animates `.count-up-stat[data-count-up]` from 0 to its target when in view.
   * Group with `[data-count-up-root]` on a parent to trigger all stats together.
   */
  #initCountUpStats() {
    const statSelector = ".count-up-stat[data-count-up]";
    const stats = [...document.querySelectorAll(statSelector)];
    if (!stats.length) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const animateStat = (el, target, duration = 1000) => {
      if (reducedMotion) {
        el.textContent = String(target);
        return;
      }

      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        el.textContent = String(Math.floor(progress * target));
        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = String(target);
      };
      requestAnimationFrame(step);
    };

    const runStats = (elements) => {
      for (const el of elements) {
        const target = parseInt(el.dataset.countUp, 10);
        if (!isNaN(target)) animateStat(el, target);
      }
    };

    const observeOnce = (root, elements) => {
      if (!elements.length) return;

      if (typeof IntersectionObserver === "undefined") {
        runStats(elements);
        return;
      }

      let animated = false;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!animated && entry.isIntersecting) {
              animated = true;
              runStats(elements);
              observer.disconnect();
            }
          }
        },
        { threshold: 0.5 },
      );

      observer.observe(root);
    };

    for (const root of document.querySelectorAll("[data-count-up-root]")) {
      const groupStats = [...root.querySelectorAll(statSelector)];
      if (!groupStats.length) continue;
      observeOnce(root, groupStats);
    }

    for (const el of stats) {
      if (el.closest("[data-count-up-root]")) continue;
      observeOnce(el, [el]);
    }
  }

  #initAutoScroll() {
    document
      .querySelectorAll('.grid-cards__blocks.slider[data-autoscroll="true"]')
      .forEach((track) => {
        const colsMobile = parseInt(track.dataset.sliderColumnsMb, 10) || 1;
        const colsTablet =
          parseInt(track.dataset.sliderColumnsTb, 10) || colsMobile;
        const colsDesktop =
          parseInt(track.dataset.sliderColumns, 10) || colsTablet;
        const desktopEnabled = track.hasAttribute("data-slider-desktop");
        const interval = parseInt(track.dataset.autoscrollInterval, 10) || 3000;
        let current = 0,
          auto = null,
          hovered = false;

        const getCols = () => {
          if (window.innerWidth >= 1024)
            return desktopEnabled ? colsDesktop : null;
          return window.innerWidth >= 540 ? colsTablet : colsMobile;
        };

        const cards = () => [...track.querySelectorAll(".grid-cards__block")];
        const stop = () => {
          clearInterval(auto);
          auto = null;
        };
        const start = () => {
          if (!auto && !hovered && getCols() !== null)
            auto = setInterval(next, interval);
        };

        const next = () => {
          const cols = getCols();
          if (cols === null) return stop();
          const items = cards();
          if (!items.length) return;
          current = (current + cols) % items.length;
          track.scrollTo({
            left: items[current].offsetLeft - track.offsetLeft,
            behavior: "smooth",
          });
        };

        track.addEventListener("mouseenter", () => {
          hovered = true;
          stop();
        });
        track.addEventListener("mouseleave", () => {
          hovered = false;
          start();
        });
        track.addEventListener(
          "scrollend",
          () => {
            const items = cards();
            const i = items.findIndex(
              (c) => c.offsetLeft - track.offsetLeft >= track.scrollLeft - 1,
            );
            if (i !== -1) current = i;
            stop();
            start();
          },
          { passive: true },
        );

        window.addEventListener(
          "resize",
          () => {
            stop();
            start();
          },
          { passive: true },
        );

        start();
      });
  }

  #initTestimonialOverlays() {
    for (const card of document.querySelectorAll("[data-testimonial-card]")) {
      const overlay = card.querySelector(".testimonial-slider__overlay");
      if (!overlay) continue;

      const setOpen = (open) => {
        card.classList.toggle("is-open", open);
        card.setAttribute("aria-expanded", open ? "true" : "false");
        card.setAttribute(
          "aria-label",
          open ? "Hide testimonial" : "Show testimonial",
        );
      };

      card.setAttribute("role", "button");
      card.setAttribute("tabindex", "0");
      card.setAttribute("aria-expanded", "false");
      card.setAttribute("aria-label", "Show testimonial");

      card.addEventListener("click", () => {
        setOpen(!card.classList.contains("is-open"));
      });

      card.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        setOpen(!card.classList.contains("is-open"));
      });
    }
  }

  #initTestimonialFilters() {
    for (const section of document.querySelectorAll(
      "[data-testimonials-slider]",
    )) {
      const filters = [
        ...section.querySelectorAll("[data-testimonial-filter]"),
      ];
      const wrap = section.querySelector("[data-testimonial-slider-wrap]");
      if (!filters.length || !wrap) continue;

      const slider = wrap.querySelector(".gallery-slider__track");
      if (!slider) continue;

      const setFilter = (value) => {
        for (const btn of filters) {
          const active = btn.dataset.testimonialFilter === value;
          btn.classList.toggle("is-active", active);
          btn.setAttribute("aria-selected", active ? "true" : "false");
        }

        for (const slide of slider.querySelectorAll(".gallery-slider__slide")) {
          const category = slide.dataset.category || "";
          const show = !value || category === value;
          slide.hidden = !show;
        }

        slider.scrollTo({ left: 0, behavior: "smooth" });
        window.dispatchEvent(new Event("resize"));
      };

      for (const btn of filters) {
        btn.addEventListener("click", () =>
          setFilter(btn.dataset.testimonialFilter || ""),
        );
      }

      const initial = filters.find((btn) =>
        btn.classList.contains("is-active"),
      );
      if (initial) setFilter(initial.dataset.testimonialFilter || "");
    }
  }

  #initReviewsCardsFilters() {
    for (const section of document.querySelectorAll(
      "[data-reviews-cards-slider]",
    )) {
      const pills = [...section.querySelectorAll("[data-review-category]")];
      const track = section.querySelector(".gallery-slider__track");
      if (!pills.length || !track) continue;

      const setFilter = (id) => {
        for (const pill of pills) {
          const active = pill.dataset.reviewCategory === id;
          pill.classList.toggle("is-active", active);
          pill.setAttribute("aria-selected", active ? "true" : "false");
        }

        for (const slide of track.querySelectorAll(".gallery-slider__slide")) {
          slide.classList.toggle(
            "hidden",
            (slide.dataset.category || "") !== id,
          );
        }

        track.scrollTo({ left: 0, behavior: "smooth" });
        window.dispatchEvent(new Event("resize"));
      };

      for (const pill of pills) {
        pill.addEventListener("click", () =>
          setFilter(pill.dataset.reviewCategory),
        );
      }
    }
  }

  #initTabsContent() {
    for (const section of document.querySelectorAll(
      "[data-tabs-content]:not([data-steps-tabs])",
    )) {
      const pills = [...section.querySelectorAll("[data-tab-pill]")];
      const panels = [...section.querySelectorAll("[data-tab-panel]")];
      if (!pills.length || !panels.length) continue;

      const activate = (id) => {
        for (const pill of pills) {
          const active = pill.dataset.tabPill === id;
          pill.classList.toggle("is-active", active);
          pill.setAttribute("aria-selected", active ? "true" : "false");
        }

        for (const panel of panels) {
          const active = panel.dataset.tabPanel === id;
          panel.classList.toggle("is-active", active);
          panel.hidden = !active;
        }
      };

      for (const pill of pills) {
        pill.addEventListener("click", () => activate(pill.dataset.tabPill));
      }
    }
  }

  #initStepsTabs() {
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const panelOf = (panels, id) =>
      panels.find((panel) => panel.dataset.tabPanel === id);
    const motionClasses = ["is-entering", "is-leaving", "is-loading"];

    const preloadImages = (panel) => {
      const imgs = [...panel.querySelectorAll(".steps-tabs__media img")];
      if (!imgs.length) return Promise.resolve();

      return Promise.all(
        imgs.map(
          (img) =>
            new Promise((resolve) => {
              const done = () => resolve();
              if (img.complete && img.naturalWidth > 0) return resolve();
              img.addEventListener("load", done, { once: true });
              img.addEventListener("error", done, { once: true });
              const src = img.currentSrc || img.src;
              if (src) {
                const loader = new Image();
                loader.addEventListener("load", done, { once: true });
                loader.addEventListener("error", done, { once: true });
                loader.src = src;
              }
              setTimeout(done, 1400);
            }),
        ),
      );
    };

    for (const section of document.querySelectorAll("[data-steps-tabs]")) {
      if (section.dataset.stepsTabsInit === "true") continue;

      const wrap = section.querySelector(".steps-tabs__panels");
      const pills = [...section.querySelectorAll("[data-tab-pill]")];
      const panels = [...section.querySelectorAll("[data-tab-panel]")];
      if (!wrap || !pills.length || !panels.length) continue;

      section.dataset.stepsTabsInit = "true";
      let activeId =
        panels.find((panel) => panel.classList.contains("is-active"))?.dataset
          .tabPanel ?? panels[0].dataset.tabPanel;
      let switching = false;

      const setPills = (id) => {
        for (const pill of pills) {
          const on = pill.dataset.tabPill === id;
          pill.classList.toggle("is-active", on);
          pill.setAttribute("aria-selected", on ? "true" : "false");
        }
      };

      const setPanel = (panel, on) => {
        panel.classList.toggle("is-active", on);
        panel.setAttribute("aria-hidden", on ? "false" : "true");
        panel.toggleAttribute("hidden", !on);
      };

      const activate = async (id) => {
        if (id === activeId || switching) return;
        setPills(id);

        if (reduceMotion) {
          for (const panel of panels) {
            panel.classList.remove(...motionClasses);
            setPanel(panel, panel.dataset.tabPanel === id);
          }
          activeId = id;
          wrap.style.minHeight = "";
          return;
        }

        const current = panelOf(panels, activeId);
        const next = panelOf(panels, id);
        if (!next) return;

        switching = true;
        wrap.style.minHeight = `${wrap.offsetHeight}px`;

        if (current) {
          current.classList.add("is-leaving");
          await wait(260);
          current.classList.remove("is-active", "is-leaving");
          setPanel(current, false);
        }

        next.classList.add("is-active", "is-entering", "is-loading");
        setPanel(next, true);
        await preloadImages(next);
        next.classList.remove("is-loading");
        requestAnimationFrame(() => next.classList.remove("is-entering"));
        await wait(360);

        activeId = id;
        wrap.style.minHeight = "";
        switching = false;
      };

      for (const panel of panels) {
        setPanel(panel, panel.dataset.tabPanel === activeId);
      }
      for (const pill of pills) {
        pill.addEventListener("click", () => activate(pill.dataset.tabPill));
      }
    }
  }

  #initVideoPopups() {
    const pause = (modal) => {
      modal.querySelector("video")?.pause();
      const video = modal.querySelector("video");
      if (video) video.currentTime = 0;
      modal.querySelector(".video-popup__iframe")?.removeAttribute("src");
    };

    const close = (modal) => {
      if (!modal || modal.hidden) return;
      pause(modal);
      modal.hidden = true;
      if (!document.querySelector(".video-popup:not([hidden])")) {
        document.body.style.overflow = "";
      }
    };

    const open = (modal) => {
      if (!modal) return;
      modal.hidden = false;
      document.body.style.overflow = "hidden";
      modal.querySelector("video")?.play().catch(() => {});
      const iframe = modal.querySelector(".video-popup__iframe");
      const embedSrc = modal.dataset.videoPopupEmbed;
      if (iframe && embedSrc) iframe.src = embedSrc;
    };

    document.addEventListener("click", (e) => {
      const openBtn = e.target.closest("[data-video-popup-open]");
      if (openBtn) {
        e.preventDefault();
        open(document.getElementById(openBtn.dataset.videoPopupOpen));
        return;
      }

      if (e.target.closest("[data-video-popup-close]")) {
        close(e.target.closest(".video-popup"));
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      for (const modal of document.querySelectorAll(".video-popup:not([hidden])")) {
        close(modal);
      }
    });

    const hash = window.location.hash;
    if (hash.startsWith("#video-popup-")) {
      open(document.getElementById(hash.slice(1)));
    }
  }

  #initFitxSteps() {
    const mq = window.matchMedia("(max-width: 1023px)");

    for (const section of document.querySelectorAll("[data-fitx-steps]")) {
      if (section.dataset.fitxStepsInit === "true") continue;

      const scrollEl = section.querySelector(".fitx-steps__scroll");
      const barTrack = section.querySelector("[data-fitx-steps-bar]");
      const viewport = barTrack?.parentElement;
      const barItems = barTrack
        ? [...barTrack.querySelectorAll("[data-fitx-steps-index]")]
        : [];
      const cards = [
        ...section.querySelectorAll("[data-fitx-steps-card]"),
      ];

      if (!scrollEl || !barTrack || !viewport || !barItems.length || !cards.length)
        continue;

      section.dataset.fitxStepsInit = "true";
      let activeIndex = 0;
      let ticking = false;
      const lastIndex = cards.length - 1;

      const setActive = (index) => {
        const next = Math.max(0, Math.min(index, lastIndex));
        if (next === activeIndex) return;
        activeIndex = next;

        for (const item of barItems) {
          item.classList.toggle(
            "is-active",
            Number.parseInt(item.dataset.fitxStepsIndex, 10) === activeIndex,
          );
        }

        for (const card of cards) {
          card.classList.toggle(
            "is-active",
            Number.parseInt(card.dataset.fitxStepsCard, 10) === activeIndex,
          );
        }
      };

      const update = () => {
        if (!mq.matches) {
          barTrack.style.transform = "";
          barTrack.style.removeProperty("--fitx-steps-slot");
          return;
        }

        const slot = viewport.clientWidth;
        barTrack.style.setProperty("--fitx-steps-slot", `${slot}px`);

        const rect = scrollEl.getBoundingClientRect();
        const travel = Math.max(1, scrollEl.offsetHeight - window.innerHeight);
        const scrolled = Math.min(travel, Math.max(0, -rect.top));
        const exact = (scrolled / travel) * lastIndex;

        barTrack.style.transform = `translateX(${-exact * slot}px)`;
        setActive(Math.round(exact));
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          update();
        });
      };

      update();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });
      mq.addEventListener("change", update);
    }
  }

  #initAddToCart() {
    document.querySelectorAll('button[name="add"].main-cta').forEach((btn) =>
      btn.addEventListener("click", async (e) => {
        e.preventDefault();
        const id = btn.getAttribute("data-id");
        if (!id) return;
        try {
          await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
          });
          if (btn.getAttribute("data-checkout") === "true") {
            window.location.href = "/checkout";
          } else if (typeof openCartDrawer === "function") {
            openCartDrawer();
          } else {
            window.location.href = "/cart";
          }
        } catch (err) {
          console.error("Add to Cart failed:", err);
        }
      }),
    );
  }
}

// Sections may append this file themselves when the template's
// conditional-assets branch did not load it. The flag makes booting idempotent
// so init() cannot run twice and double-bind every handler.
document.addEventListener("DOMContentLoaded", function () {
  if (window.__reusableComponentsBooted) return;
  window.__reusableComponentsBooted = true;
  const reusableComponents = new ReusableComponents();
  reusableComponents.init();
});
